import { Component, inject, input, InputSignal, OnDestroy, OnInit, output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MapLayerConfiguration, MapLayerSource, MapLayerType } from '../../../../../shared/models/map-layer.model';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatCheckbox } from '@angular/material/checkbox';
import { firstValueFrom, Subscription } from 'rxjs';
import { ExtAuthService } from '../../../../../data/ext-auth/ext-auth.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { DecimalPipe } from '@angular/common';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { HttpClient } from '@angular/common/http';

interface ArcGISLayerInfo {
  id: number;
  name: string;
  defaultVisibility: boolean;
  selected?: boolean;
}

interface ArcGISServiceInfo {
  layers?: ArcGISLayerInfo[];
  documentInfo?: {
    Title?: string;
  };
  mapName?: string;
}

@Component({
  selector: 'app-map-layer-editor',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatError,
    MatInput,
    MatSelect,
    MatOption,
    MatHint,
    MatCheckbox,
    MatProgressSpinner,
    MatIconButton,
    MatTooltip,
    MatIcon,
    MatSlider,
    MatSliderThumb,
    DecimalPipe,
    CdkTextareaAutosize,
    MatButton,
    FormsModule,
  ],
  templateUrl: './map-layer-editor.component.html',
  styleUrl: './map-layer-editor.component.scss',
})
export class MapLayerEditorComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly extAuthService = inject(ExtAuthService);
  private readonly toast = inject(ToastService);
  private readonly http = inject(HttpClient);

  formGroup: FormGroup;
  verifyingCredentials = false;

  mapLayer: InputSignal<MapLayerConfiguration> = input();
  layerChanged = output<{ valid: boolean; layer: MapLayerConfiguration }>();

  /** ArcGIS helper variables **/
  private readonly defaultDpi = 96;
  private readonly defaultFormat = 'png32';

  urlError = '';
  fetchError = '';
  isLoading = false;

  serviceLayers: ArcGISLayerInfo[] = [];
  selectedLayerIds: number[] = [];
  serviceInfo = '';
  /** ArcGIS helper variables **/

  protected readonly MapLayerSource = MapLayerSource;
  private readonly subscriptions: Subscription[] = [];

  constructor() {}

  ngOnInit(): void {
    // init form group
    this.formGroup = this.fb.group({
      name: this.fb.control(this.mapLayer()?.name, {
        validators: Validators.required,
        updateOn: 'blur',
      }),
      source: this.fb.control(this.mapLayer()?.source),
      url: this.fb.control(this.mapLayer()?.url, {
        validators: Validators.required,
      }),
      arcGisBaseUrl: this.fb.control(''),
      arcGisFormat: this.fb.control(this.defaultFormat),
      arcGisDpi: this.fb.control(this.defaultDpi),
      configuration: this.fb.control(this.mapLayer()?.configuration, {
        validators: Validators.required,
      }),
      useAsBaseMap: this.fb.control(this.mapLayer()?.useAsBaseMap, {}),
      useCredentials: this.fb.control(this.mapLayer()?.useCredentials),
      username: this.fb.control(this.mapLayer()?.username),
      password: this.fb.control(this.mapLayer()?.password),
      opacity: this.fb.control(this.mapLayer()?.opacity),
    });

    if (this.formGroup.controls.useAsBaseMap.value) {
      this.formGroup.controls.useCredentials.disable();
    }

    // initialize disabled inputs - not validated
    switch (this.formGroup.controls.source.value) {
      case MapLayerSource.RouteImport:
        this.formGroup.controls.source.disable({emitEvent: true});
        this.formGroup.controls.url.disable();
        this.formGroup.controls.configuration.disable();
        break;
      case MapLayerSource.MapLibreConfig:
        this.formGroup.controls.url.disable();
        break;
      case MapLayerSource.TileUrl:
      case MapLayerSource.ArcGisExport:
        this.formGroup.controls.configuration.disable();
        break;
      default:
        break;
    }

    // init ArcGIS variables
    if (this.mapLayer()?.url && this.mapLayer()?.source === MapLayerSource.ArcGisExport) {
      const index = this.mapLayer().url.indexOf('MapServer');
      if (index < 0) {
        console.warn('Invalid ArcGIS url!');
      } else {
        this.formGroup.controls.arcGisBaseUrl.setValue(index !== -1 ? this.mapLayer().url.substring(0, index + 'MapServer'.length) : '');
        const configLayerIds = this.getLayerIds(this.mapLayer().url);

        this.fetchLayers().then(() => {
          if (configLayerIds) {
            this.serviceLayers.forEach(serviceLayer => {
              serviceLayer.selected = configLayerIds.includes(serviceLayer.id);
            });
            this.updateSelectedLayers();
          }
        });

        this.formGroup.controls.arcGisDpi.setValue(this.getDpi(this.mapLayer().url));
        this.formGroup.controls.arcGisFormat.setValue(this.getFormat(this.mapLayer().url));
      }
    }

    // source onChange
    this.subscriptions.push(
      this.formGroup.controls.source.valueChanges.subscribe(source => {
        if (source === MapLayerSource.MapLibreConfig) {
          this.formGroup.controls.url.disable();
          this.formGroup.controls.configuration.enable();
        } else {
          this.formGroup.controls.url.enable();
          this.formGroup.controls.configuration.disable();
          if (source === MapLayerSource.ArcGisExport) {
            this.formGroup.controls.url.setValue(null);
          }
        }
      })
    );

    // useAsBaseMap onChange
    this.subscriptions.push(
      this.formGroup.controls.useAsBaseMap.valueChanges.subscribe(useAsBaseMap => {
        if (useAsBaseMap) {
          this.formGroup.controls.useCredentials.disable();
          this.formGroup.controls.useCredentials.setValue(false);
          this.formGroup.controls.username.setValue(null);
          this.formGroup.controls.password.setValue(null);
        } else {
          this.formGroup.controls.useCredentials.enable();
        }
      })
    );

    // useCredentials onChange
    this.subscriptions.push(
      this.formGroup.controls.useCredentials.valueChanges.subscribe(useCredentials => {
        if (!useCredentials) {
          this.formGroup.controls.username.setValue(null);
          this.formGroup.controls.password.setValue(null);
        }
      })
    );

    // arcGisFormat onChange
    this.subscriptions.push(
      this.formGroup.controls.arcGisFormat.valueChanges.subscribe(arcGisFormat => {
        this.updateUrlBasedOnArcGisInputs();
      })
    );

    // arcGisDpi onChange
    this.subscriptions.push(
      this.formGroup.controls.arcGisDpi.valueChanges.subscribe(arcGisDpi => {
        this.updateUrlBasedOnArcGisInputs();
      })
    );

    // emits changes to parent component
    this.formGroup.valueChanges.subscribe(formValue => {
      this.layerChanged.emit({
        valid: this.formGroup.valid,
        layer: this.formToMapLayer(formValue),
      });
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription?.unsubscribe());
  }

  private formToMapLayer(f: any): MapLayerConfiguration {
    const layer = Object.assign({}, this.mapLayer());
    layer.source = f.source ?? MapLayerSource.RouteImport; // RouteImport may be disabled
    switch (layer.source) {
      case MapLayerSource.RouteImport:
        layer.type = MapLayerType.ROUTE;
        break;
      case MapLayerSource.ArcGisExport:
        layer.type = MapLayerType.RASTER;
        layer.url = f.url;
        layer.configuration = null;
        break;
      case MapLayerSource.MapLibreConfig:
        layer.type = MapLayerType.VECTOR;
        layer.url = null;
        layer.configuration = f.configuration;
        break;
      case MapLayerSource.TileUrl:
      default:
        layer.type = MapLayerType.RASTER;
        layer.url = f.url;
        layer.configuration = null;
    }

    layer.name = f.name;
    if (layer.type === MapLayerType.RASTER) {
      layer.useAsBaseMap = f.useAsBaseMap;
      layer.opacity = f.opacity;
      layer.username = f.username;
      layer.password = f.password;
    } else {
      layer.useAsBaseMap = null;
      layer.opacity = null;
      layer.username = null;
      layer.password = null;
    }

    return layer;
  }

  testCredentials() {
    this.verifyingCredentials = true;
    this.extAuthService
      .verifyEsriCredentials(
        this.formGroup.controls.arcGisBaseUrl.value,
        this.formGroup.controls.username.value,
        this.formGroup.controls.password.value
      )
      .then(response => {
        if (response.data) {
          this.toast.short('Connection is OK!');
        } else {
          this.toast.long('Error on connection!');
        }
      })
      .finally(() => (this.verifyingCredentials = false));
  }

  private getLayerIds(url: string): number[] {
    const params = new URL(url).searchParams;
    const layersParam = params.get('layers'); // e.g. "show:0,1,2,3,4,..."
    if (!layersParam) {
      return [];
    }

    const match = layersParam.match(/^show:(.+)$/);
    if (!match) {
      return [];
    }

    return match[1].split(',').map(Number);
  }

  private getDpi(url: string): number {
    const params = new URL(url).searchParams;
    const dpiParam = params.get('dpi');
    if (!dpiParam) {
      return this.defaultDpi;
    }
    return +dpiParam;
  }

  private getFormat(url: string): string {
    const params = new URL(url).searchParams;
    const formatParam = params.get('format');
    if (!formatParam) {
      return this.defaultFormat;
    }
    return formatParam;
  }

  validateUrl(): void {
    this.urlError = '';

    if (!this.formGroup.controls.arcGisBaseUrl.value) {
      return;
    }

    const trimmedUrl = this.formGroup.controls.arcGisBaseUrl.value.trim();

    if (!trimmedUrl.match(/^https?:\/\//i)) {
      this.urlError = 'URL must start with http:// or https://';
      return;
    }

    if (!trimmedUrl.match(/\/(MapServer|FeatureServer)\/?$/i)) {
      this.urlError = 'URL must end with /MapServer or /FeatureServer';
      return;
    }
  }

  async fetchLayers(): Promise<void> {
    this.validateUrl();
    if (this.urlError) {
      console.warn(this.urlError);
      return;
    }

    this.isLoading = true;
    this.fetchError = '';
    this.serviceLayers = [];
    this.serviceInfo = '';

    const cleanUrl = this.formGroup.controls.arcGisBaseUrl.value.trim().replace(/\/$/, '');
    const serviceUrl = `${cleanUrl}?f=pjson`;

    try {
      const response = await firstValueFrom(this.http.get<ArcGISServiceInfo>(serviceUrl));

      if (response && response.layers && response.layers.length > 0) {
        this.serviceLayers = response.layers.map(layer => ({
          ...layer,
          selected: layer.defaultVisibility,
        }));
        const group = new FormGroup({});
        this.serviceLayers.forEach(serviceLayer => {
          group.addControl(serviceLayer.id.toString(), this.fb.control(serviceLayer.selected));
        });
        this.formGroup.addControl('arcGisServiceLayers', group);

        // Set service info
        if (response.documentInfo?.Title) {
          this.serviceInfo = response.documentInfo.Title;
          // this.layerName = this.serviceInfo;
        } else if (response.mapName) {
          this.serviceInfo = response.mapName;
          // this.layerName = this.serviceInfo;
        } else {
          this.serviceInfo = 'Service loaded successfully';
        }

        this.updateSelectedLayers();
      } else {
        this.fetchError = 'No layers found in this service';
      }
    } catch (error: any) {
      console.error('Error fetching layers:', error);
      this.fetchError = `Failed to fetch layers: ${error.message || 'Unknown error'}.`;
    } finally {
      this.isLoading = false;
    }
  }

  updateSelectedLayers(): void {
    const selections = this.formGroup?.value?.arcGisServiceLayers ?? [];
    this.selectedLayerIds = Object.keys(selections)
      .filter(id => selections[id])
      .map(Number)
      .sort((a, b) => a - b);
    this.updateUrlBasedOnArcGisInputs();
  }

  setAll(checked: boolean): void {
    const all = Object.fromEntries(this.serviceLayers.map(layer => [layer.id.toString(), checked]));
    this.formGroup.get('arcGisServiceLayers')?.patchValue(all);
    this.updateSelectedLayers();
  }

  selectDefault(): void {
    const patch = Object.fromEntries(this.serviceLayers.map(layer => [layer.id.toString(), (layer.selected = layer.defaultVisibility)]));
    this.formGroup.get('arcGisServiceLayers')?.patchValue(patch);
    this.updateSelectedLayers();
  }

  isValid(): boolean {
    return this.formGroup.controls.arcGisBaseUrl.value.trim() !== '' && !this.urlError;
  }

  buildTileUrl(): string {
    const cleanUrl = this.formGroup.controls.arcGisBaseUrl.value.trim().replace(/\/$/, '');
    const params: string[] = [
      `dpi=${this.formGroup.controls.arcGisDpi.value}`,
      'transparent=true',
      `format=${this.formGroup.controls.arcGisFormat.value}`,
      'bboxSR=3857',
      'imageSR=3857',
      'size=256,256',
      'f=image',
    ];

    // Add selected layers
    if (this.selectedLayerIds.length > 0) {
      params.push(`layers=show:${this.selectedLayerIds.join(',')}`);
    }

    // MapLibre uses {bbox-epsg-3857} placeholder - don't encode the braces
    params.push('bbox={bbox-epsg-3857}');

    return `${cleanUrl}/export?${params.join('&')}`;
  }

  updateUrlBasedOnArcGisInputs(): void {
    if (!this.isValid()) {
      return;
    }

    this.formGroup.controls.url.setValue(this.buildTileUrl());
  }
}
